import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ChapterEnum, PriceListUerRowInput, WorkOrderChangeObjectIdInput } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerAddUersToWorkOrderChangeObjectsGql } from '../workOrderChangeAddPricesQueries';

const requestId = 'addPricesToWorkOrderChange';

interface IAddPricesProps {
    onClose: () => void;
    reloadWorkOrderTable: (objectIds: number[]) => void;
    workOrderChangeId: number;
    workOrderChangeObjectIds: WorkOrderChangeObjectIdInput[];
    uerInputs: PriceListUerRowInput[] | undefined;
    constructionTypeId: number;
    chapter: ChapterEnum | undefined;
}

export const useAddPricesToWorkOrderChange = (props: IAddPricesProps) => {
    const dispatch = useDispatch();

    const {
        reloadWorkOrderTable,
        onClose,
        workOrderChangeObjectIds,
        workOrderChangeId,
        constructionTypeId,
        chapter,
        uerInputs,
    } = props;

    const objectIds = useMemo(
        () => workOrderChangeObjectIds?.map((o) => o.workOrderObjectId),
        [workOrderChangeObjectIds],
    );

    const onSuccess = useCallback(() => {
        onClose();
        reloadWorkOrderTable(objectIds);
    }, [objectIds, onClose, reloadWorkOrderTable]);

    const [, status, , request] = useRequest({
        requestId,
        query: uerAddUersToWorkOrderChangeObjectsGql,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const isLoading = status === RequestStatus.FETCHING;
    const [reset] = useResetStoreData({ requestId });

    useEffect(
        () => () => {
            reset(undefined);
        },
        [reset],
    );
    const addPricesToWorkOrderChangeHandler = useCallback(() => {
        if (chapter && uerInputs) {
            dispatch(
                request({
                    workOrderChangeObjectIds,
                    chapter,
                    workOrderChangeId,
                    constructionTypeId,
                    uerInputs,
                }),
            );
        }
    }, [chapter, constructionTypeId, dispatch, request, uerInputs, workOrderChangeId, workOrderChangeObjectIds]);

    return {
        addPricesToWorkOrderChangeHandler,
        isLoading,
    };
};
